// Agent config reviews — Repo Governance's pull-request inbox for AI agent files (ADR-016).
//
// One row per pull-request review, worst first, with the author named. The
// question a security lead opens this page to answer is "did anyone change what
// our coding agents are allowed to do, and was it dangerous", so severity and
// the person come before anything else in the row.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@/components/ui/link";
import { useRouter } from "@/lib/navigation";
import * as React from "react";

import { Actor, SeverityBadge, VerdictBadge, When } from "@/components/governance/bits";
import { GovernanceHeader, useIsGovernanceAdmin } from "@/components/governance/nav";
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { setGovernanceSettings } from "@/lib/api/endpoints";
import {
  useGithubInstallation,
  useGovernanceSettings,
  useGovernedRepos,
  useLlmModels,
  usePrReviews,
} from "@/lib/query/hooks";
import { formatTokens, formatUsd } from "@/lib/format-usage";
import { qk } from "@/lib/query/keys";
import type { GovernanceVerdict, ReviewStatus } from "@/types/api";

const VERDICTS: { value: GovernanceVerdict | ""; label: string }[] = [
  { value: "", label: "All" },
  { value: "fail", label: "Risky" },
  { value: "warn", label: "Needs a look" },
  { value: "pass", label: "Clean" },
];

export default function GovernancePage() {
  const router = useRouter();
  const [verdict, setVerdict] = React.useState<GovernanceVerdict | "">("");
  const [status, setStatus] = React.useState<ReviewStatus | "">("");
  const [repoId, setRepoId] = React.useState<number | undefined>(undefined);

  const installation = useGithubInstallation();
  // No refresh from GitHub: this only needs repo names for the filter, and a
  // dropdown is not worth an API round-trip to GitHub on every page view.
  const repos = useGovernedRepos(false);
  const reviews = usePrReviews({
    verdict: verdict || undefined,
    status: status || undefined,
    repoId,
  });

  const filtered = verdict !== "" || status !== "" || repoId !== undefined;
  const clearFilters = () => {
    setVerdict("");
    setStatus("");
    setRepoId(undefined);
  };

  if (installation.isLoading) return <Skeleton className="h-64 w-full" />;

  // Nothing to review until a GitHub org is connected — send them there rather
  // than showing an empty table with no explanation.
  if (installation.data && !installation.data.connected) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState
          title="Connect a GitHub organisation to start governing repositories"
          hint="Aviary watches .claude, .cursor, CLAUDE.md and .mcp.json in the repositories you choose, and reviews every pull request that changes them."
          action={<Button onClick={() => router.push("/github")}>Connect GitHub</Button>}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header />

      <AnalyzerModel />

      <div className="flex flex-wrap items-center gap-2">
        {VERDICTS.map((option) => (
          <Button
            key={option.label}
            size="sm"
            variant={verdict === option.value ? "default" : "outline"}
            onClick={() => setVerdict(option.value)}
          >
            {option.label}
          </Button>
        ))}
        <span className="mx-2 h-5 w-px bg-border" />
        <Button
          size="sm"
          variant={status === "failed" ? "default" : "outline"}
          onClick={() => setStatus(status === "failed" ? "" : "failed")}
          title="Reviews that could not run — usually a GitHub permission or connectivity problem."
        >
          Scan failures
        </Button>
        <select
          value={repoId ?? ""}
          onChange={(e) => setRepoId(e.target.value ? Number(e.target.value) : undefined)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          aria-label="Filter by repository"
        >
          <option value="">All repositories</option>
          {(repos.data ?? [])
            .filter((r) => r.enabled)
            .map((repo) => (
              <option key={repo.github_repo_id} value={repo.github_repo_id}>
                {repo.full_name}
              </option>
            ))}
        </select>
      </div>

      {reviews.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : reviews.isError ? (
        <ErrorState error={reviews.error} onRetry={() => void reviews.refetch()} />
      ) : (reviews.data ?? []).length === 0 ? (
        // Two very different situations, and conflating them reads as data
        // loss: "nothing has ever been reviewed" vs "your filter matched
        // nothing". The second needs a way back, not an explanation of how
        // reviews get created.
        filtered ? (
          <EmptyState
            title="No reviews match these filters"
            hint="Nothing here with the selected verdict, status and repository. Other reviews may still exist."
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No agent-config changes reviewed yet"
            hint="Open a pull request that touches .claude, .cursor, CLAUDE.md or .mcp.json in an enabled repository and it will appear here within seconds."
          />
        )
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Risk</TH>
                  <TH>Verdict</TH>
                  <TH>Pull request</TH>
                  <TH>Repository</TH>
                  <TH>Changed by</TH>
                  <TH>Findings</TH>
                  <TH>AI tokens / cost</TH>
                  <TH>When</TH>
                </TR>
              </THead>
              <TBody>
                {(reviews.data ?? []).map((review) => (
                  <TR
                    key={review.id}
                    className="cursor-pointer"
                    onClick={() => router.push(`/governance/${review.id}`)}
                  >
                    <TD>
                      <SeverityBadge severity={review.max_severity} />
                    </TD>
                    <TD>
                      <VerdictBadge verdict={review.verdict} status={review.status} />
                    </TD>
                    <TD className="max-w-xs">
                      <div className="truncate font-medium">
                        {review.pr_title ?? `Pull request #${review.pr_number}`}
                      </div>
                      <div className="text-xs text-muted-foreground">#{review.pr_number}</div>
                    </TD>
                    <TD className="text-sm">{review.repo_full_name}</TD>
                    <TD>
                      <Actor login={review.author_login} />
                    </TD>
                    <TD className="text-sm">
                      {review.finding_count || "—"}
                      {review.analyzer_error ? (
                        <span
                          className="ml-1 text-xs text-muted-foreground"
                          title="The AI pass did not complete — deterministic rule checks only."
                        >
                          (partial)
                        </span>
                      ) : null}
                    </TD>
                    <TD className="text-sm tabular-nums">
                      {review.analyzer_model ? (
                        <span title={review.analyzer_model}>
                          {formatTokens(
                            (review.analyzer_tokens_prompt ?? 0) +
                              (review.analyzer_tokens_completion ?? 0),
                          )}{" "}
                          · {formatUsd(review.analyzer_cost_usd)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">rules only</span>
                      )}
                    </TD>
                    <TD>
                      <When iso={review.created_at} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Header() {
  return (
    <GovernanceHeader description="Pull requests that change the files deciding what AI coding agents may do in your repositories — permissions, tool grants, MCP servers, hooks, and agent instructions." />
  );
}

/** Which configured LLM provider reviews pull requests.
 *
 * Lives on this page rather than under Settings because it is the one knob
 * that changes what these findings *are* — the deterministic rules run
 * regardless, but everything badged "AI" comes from this model. Seeing it next
 * to the results is the point.
 */
function AnalyzerModel() {
  const qc = useQueryClient();
  const isAdmin = useIsGovernanceAdmin();
  const current = useGovernanceSettings();
  const models = useLlmModels();
  const [selected, setSelected] = React.useState<string | null>(null);

  const value = selected ?? current.data?.llm_model_slug ?? "";

  const save = useMutation({
    mutationFn: (slug: string) => setGovernanceSettings(slug || null),
    onSuccess: () => {
      toast.success("Analysis model updated.");
      void qc.invalidateQueries({ queryKey: qk.governanceSettings });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (current.isLoading) return <Skeleton className="h-14 w-full" />;

  const available = models.data ?? [];

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <span className="text-sm font-medium">AI analysis model</span>
      {available.length === 0 ? (
        <span className="text-sm text-muted-foreground">
          None configured —{" "}
          <Link href="/llm-models" className="underline underline-offset-4">
            add an LLM provider
          </Link>{" "}
          to enable AI analysis.
        </span>
      ) : (
        <>
          <select
            value={value}
            onChange={(e) => {
              setSelected(e.target.value);
              save.mutate(e.target.value);
            }}
            disabled={save.isPending || !isAdmin}
            className="h-9 min-w-[16rem] rounded-md border border-input bg-background px-3 text-sm"
            aria-label="Model used to analyse pull requests"
          >
            <option value="">None — rule checks only</option>
            {available.map((m) => (
              <option key={m.slug} value={m.slug}>
                {m.name} — {m.provider} / {m.model}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">
            {value
              ? "Reviews call this model with its stored key."
              : "No model selected, so reviews run the deterministic rule checks only."}
          </span>
        </>
      )}
    </div>
  );
}
